import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import {
  closeStandardModal,
  openStandardModal,
} from "../../../common/state/slice/modal/modalSlice";
import { buildHelpDocsUrl } from "../../../common/util/helpDocsLighthouse/buildHelpDocsUrl";
import {
  closeChecklistCompletePrompt,
  selectChecklistCompletePromptOpen,
  selectOnboardingAudience,
} from "../state/onboardingSlice";

export const ONBOARDING_CHECKLIST_COMPLETE_MODAL_ID = "onboarding-checklist-complete";
export const HELPDOCS_HOME_PATH = "/l/fr";
export const WIXARPEDIA_ROUTE = "/chat/wixarpedia";

const INLINE_LINK_CLASS =
  "font-medium text-green-800 underline underline-offset-2 hover:text-green-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500";

export const ChecklistCompletePrompt = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const promptOpen = useAppSelector(selectChecklistCompletePromptOpen);
  const audience = useAppSelector(selectOnboardingAudience);
  const closeButtonRef = useRef<HTMLElement | null>(null);
  const isEnduser = audience === "enduser";

  useEffect(() => {
    if (promptOpen) {
      dispatch(openStandardModal(ONBOARDING_CHECKLIST_COMPLETE_MODAL_ID));
    }
  }, [promptOpen, dispatch]);

  if (!promptOpen) {
    return null;
  }

  const close = (): void => {
    dispatch(closeStandardModal(ONBOARDING_CHECKLIST_COMPLETE_MODAL_ID));
    dispatch(closeChecklistCompletePrompt());
  };

  const openWixarpedia = (): void => {
    close();
    navigate(WIXARPEDIA_ROUTE);
  };

  return (
    <ModalStandard
      id={ONBOARDING_CHECKLIST_COMPLETE_MODAL_ID}
      className="w-[30rem]"
      initialFocus={closeButtonRef}
      onClose={() => dispatch(closeChecklistCompletePrompt())}
    >
      <div className="modalContent personalize-scroll-visible">
        <h2 className="pt-3 pb-1 font-medium text-lg text-balance">
          {t("onboarding.checklist.completePrompt.title")}
        </h2>
        <p className="pb-3 text-gray-600 whitespace-pre-line">
          <Trans
            i18nKey={
              isEnduser
                ? "onboarding.checklist.completePrompt.bodyEnduser"
                : "onboarding.checklist.completePrompt.body"
            }
            components={{
              docs: (
                <a
                  className={INLINE_LINK_CLASS}
                  href={buildHelpDocsUrl(HELPDOCS_HOME_PATH)}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              wixarpedia: (
                <button type="button" className={INLINE_LINK_CLASS} onClick={openWixarpedia} />
              ),
            }}
          />
        </p>
      </div>
      <div className="flex justify-end items-center mt-4">
        <button
          ref={closeButtonRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="btn-primary-fill"
          onClick={close}
        >
          {t("general.close")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default ChecklistCompletePrompt;
